import React, { useState } from 'react';
import defaultParamsByType from '../../Config/defaultParamsByType';

// ─── Types ────────────────────────────────────────────────────────────────────

interface WizardItem {
  type: string;
  label: string;
}

interface WizardWorkspace {
  id: string;
  name: string;
  items: string[]; // fabric item types
}

interface WizardDomain {
  id: string;
  name: string;
  emoji: string;
  workspaces: WizardWorkspace[];
}

interface WizardState {
  capacityName: string;
  capacitySku: string;
  domains: WizardDomain[];
}

export interface MultiDomainResult {
  diagramData: { nodes: any[]; edges: any[] };
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (result: MultiDomainResult) => void;
}

// ─── Config ───────────────────────────────────────────────────────────────────

const SKU_OPTIONS = ['F2', 'F4', 'F8', 'F16', 'F32', 'F64', 'F128', 'F256'];

const SUGGESTED_DOMAINS = [
  { name: 'Finance',     emoji: '💰' },
  { name: 'Commercial',  emoji: '📈' },
  { name: 'RH',         emoji: '👥' },
  { name: 'IT / Data',  emoji: '⚙️' },
  { name: 'Marketing',  emoji: '📣' },
  { name: 'Operations', emoji: '🏭' },
  { name: 'Risque',     emoji: '🛡️' },
];

const ITEM_CATALOG: WizardItem[] = [
  { type: 'fabric_lakehouse',         label: 'Lakehouse' },
  { type: 'fabric_pipeline',          label: 'Pipeline' },
  { type: 'fabric_notebook',          label: 'Notebook' },
  { type: 'fabric_semantic_model',    label: 'Semantic Model' },
  { type: 'fabric_report',            label: 'Power BI Report' },
  { type: 'fabric_warehouse',         label: 'Warehouse' },
  { type: 'fabric_dataflow',          label: 'Dataflow Gen2' },
  { type: 'fabric_eventstream',       label: 'EventStream' },
  { type: 'fabric_eventhouse',        label: 'Eventhouse' },
  { type: 'fabric_ml_model',          label: 'ML Model' },
  { type: 'fabric_deployment_pipeline', label: 'Deployment Pipeline' },
];

const ITEM_ICONS: Record<string, string> = {
  fabric_lakehouse:           '/icons/fabric/lakehouse_64_item.svg',
  fabric_pipeline:            '/icons/fabric/pipeline_64_item.svg',
  fabric_notebook:            '/icons/fabric/notebook_64_item.svg',
  fabric_semantic_model:      '/icons/fabric/semantic_model_64_item.svg',
  fabric_report:              '/icons/fabric/power_bi_48_color.svg',
  fabric_warehouse:           '/icons/fabric/data_warehouse_64_item.svg',
  fabric_dataflow:            '/icons/fabric/dataflow_gen2_64_item.svg',
  fabric_eventstream:         '/icons/fabric/eventstream_64_item.svg',
  fabric_eventhouse:          '/icons/fabric/event_house_64_item.svg',
  fabric_ml_model:            '/icons/fabric/data_science_48_color.svg',
  fabric_deployment_pipeline: '/icons/fabric/copy_job_64_item.svg',
};

const DOMAIN_COLORS = ['#F3E8FF', '#E8F0FF', '#FFF3E8', '#E8FFF0', '#FFF8E8', '#F0F8FF', '#FFE8F0'];
const DOMAIN_BORDER_COLORS = ['#C084FC', '#93C5FD', '#FCA05A', '#6EE7B7', '#FCD34D', '#67E8F9', '#F9A8D4'];

// ─── Diagram generator ────────────────────────────────────────────────────────

function generateDiagram(state: WizardState): { nodes: any[]; edges: any[] } {
  const nodes: any[] = [];
  const edges: any[] = [];

  const DOMAIN_W = 380;
  const DOMAIN_GAP = 30;
  const CAP_PAD_X = 20;
  const CAP_PAD_TOP = 60;
  const WS_MARGIN = 15;
  const ITEM_COL_W = 160;
  const ITEM_GAP_X = 10;
  const ITEM_ROW_H = 130;
  const ITEM_START_Y = 85;
  const WS_PAD_X = 20;
  const WS_PAD_BOTTOM = 25;
  const WS_HEADER = 55;
  const DOM_HEADER = 50;
  const DOM_PAD_BOTTOM = 20;
  const WS_GAP = 15;

  // Calculate workspace height based on item count
  const wsHeight = (items: string[]) => {
    const rows = Math.max(1, Math.ceil(items.length / 2));
    return WS_HEADER + ITEM_START_Y + rows * ITEM_ROW_H - (ITEM_ROW_H - 60) + WS_PAD_BOTTOM;
  };

  // Calculate domain height
  const domHeight = (workspaces: WizardWorkspace[]) => {
    if (workspaces.length === 0) return DOM_HEADER + 100 + DOM_PAD_BOTTOM;
    const totalWs = workspaces.reduce((acc, ws) => acc + wsHeight(ws.items), 0);
    return DOM_HEADER + WS_MARGIN + totalWs + (workspaces.length - 1) * WS_GAP + DOM_PAD_BOTTOM;
  };

  const maxDomH = Math.max(...state.domains.map(d => domHeight(d.workspaces)), 300);
  const totalCapW = CAP_PAD_X * 2 + state.domains.length * DOMAIN_W + (state.domains.length - 1) * DOMAIN_GAP;
  const totalCapH = CAP_PAD_TOP + maxDomH + 20;

  const capId = 'cap-md';
  const capDefaults = (defaultParamsByType as any)['fabric_capacity'] || {};
  nodes.push({
    id: capId,
    type: 'group',
    position: { x: 0, y: 0 },
    width: totalCapW,
    height: totalCapH,
    style: { backgroundColor: '#E0F7FA' },
    data: {
      type: 'fabric_capacity', resourceType: 'fabric_capacity',
      label: state.capacityName || 'Fabric Capacity',
      name: (state.capacityName || 'fabric-capacity').toLowerCase().replace(/\s+/g, '-'),
      icon: '/icons/fabric/fabric_capacity.svg',
      color: '#E0F7FA',
      params: {
        ...capDefaults,
        display_name: (state.capacityName || 'fabric-capacity').toLowerCase().replace(/\s+/g, '-'),
        name: (state.capacityName || 'fabric-capacity').toLowerCase().replace(/\s+/g, '-'),
        sku: state.capacitySku,
        location: 'West Europe',
      },
      properties: {}, config: {},
    },
  });

  state.domains.forEach((domain, dIdx) => {
    const domId = `dom-${domain.id}`;
    const domX = CAP_PAD_X + dIdx * (DOMAIN_W + DOMAIN_GAP);
    const domY = CAP_PAD_TOP;
    const domH = domHeight(domain.workspaces);
    const domDefaults = (defaultParamsByType as any)['fabric_domain'] || {};

    nodes.push({
      id: domId,
      type: 'group',
      position: { x: domX, y: domY },
      width: DOMAIN_W,
      height: domH,
      parentNode: capId,
      extent: 'parent',
      expandParent: true,
      style: { backgroundColor: DOMAIN_COLORS[dIdx % DOMAIN_COLORS.length] },
      data: {
        type: 'fabric_domain', resourceType: 'fabric_domain',
        label: `${domain.emoji} ${domain.name}`,
        name: domain.name,
        icon: '/icons/azure/10007-icon-service-Resource-Groups.svg',
        color: DOMAIN_COLORS[dIdx % DOMAIN_COLORS.length],
        params: {
          ...domDefaults,
          name: domain.name.toLowerCase().replace(/\s+/g, '-'),
          display_name: domain.name,
        },
        properties: {}, config: {},
      },
    });

    let wsYOffset = WS_MARGIN + DOM_HEADER;
    domain.workspaces.forEach((ws) => {
      const wsId = `ws-${ws.id}`;
      const wsH = wsHeight(ws.items);
      const wsDefaults = (defaultParamsByType as any)['fabric_workspace'] || {};

      nodes.push({
        id: wsId,
        type: 'group',
        position: { x: WS_MARGIN, y: wsYOffset },
        width: DOMAIN_W - WS_MARGIN * 2,
        height: wsH,
        parentNode: domId,
        extent: 'parent',
        expandParent: true,
        style: { backgroundColor: '#E8F5E9' },
        data: {
          type: 'fabric_workspace', resourceType: 'fabric_workspace',
          label: ws.name,
          name: ws.name,
          icon: '/icons/fabric/group_workspace_64_non-item.svg',
          color: '#E8F5E9',
          params: {
            ...wsDefaults,
            display_name: ws.name.toLowerCase().replace(/\s+/g, '-'),
            name: ws.name.toLowerCase().replace(/\s+/g, '-'),
            capacityId: capId,
          },
          properties: {}, config: {},
        },
      });

      ws.items.forEach((itemType, iIdx) => {
        const itemId = `item-${ws.id}-${iIdx}`;
        const col = iIdx % 2;
        const row = Math.floor(iIdx / 2);
        const itemX = WS_PAD_X + col * (ITEM_COL_W + ITEM_GAP_X);
        const itemY = ITEM_START_Y + row * ITEM_ROW_H;
        const itemLabel = ITEM_CATALOG.find(c => c.type === itemType)?.label || itemType;
        const itemDefaults = (defaultParamsByType as any)[itemType] || {};
        // Prefix with workspace slug to avoid duplicate-name errors across workspaces
        const wsSlug = ws.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        const itemSlug = itemLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        const uniqueName = `${wsSlug}-${itemSlug}`;

        nodes.push({
          id: itemId,
          type: 'custom',
          position: { x: itemX, y: itemY },
          parentNode: wsId,
          extent: 'parent',
          expandParent: true,
          data: {
            type: itemType, resourceType: itemType,
            label: itemLabel,
            name: itemLabel,
            icon: ITEM_ICONS[itemType] || '/icons/fabric/lakehouse_64_item.svg',
            editing: false,
            params: {
              ...itemDefaults,
              display_name: uniqueName,
              name: uniqueName,
              workspaceId: wsId,
            },
            properties: {}, config: {},
          },
        });
      });

      // Auto-wire items within workspace: pipeline→lakehouse, lakehouse→semantic_model, semantic_model→report
      const itemIds = ws.items.map((_, i) => `item-${ws.id}-${i}`);
      const lhIdx = ws.items.findIndex(t => t === 'fabric_lakehouse');
      const plIdx = ws.items.findIndex(t => t === 'fabric_pipeline');
      const dfIdx = ws.items.findIndex(t => t === 'fabric_dataflow');
      const smIdx = ws.items.findIndex(t => t === 'fabric_semantic_model');
      const rptIdx = ws.items.findIndex(t => t === 'fabric_report');
      const nbIdx = ws.items.findIndex(t => t === 'fabric_notebook');

      if (plIdx >= 0 && lhIdx >= 0) edges.push({ id: `e-${ws.id}-pl-lh`, source: itemIds[plIdx], target: itemIds[lhIdx] });
      if (dfIdx >= 0 && lhIdx >= 0) edges.push({ id: `e-${ws.id}-df-lh`, source: itemIds[dfIdx], target: itemIds[lhIdx] });
      if (lhIdx >= 0 && nbIdx >= 0) edges.push({ id: `e-${ws.id}-lh-nb`, source: itemIds[lhIdx], target: itemIds[nbIdx] });
      if (lhIdx >= 0 && smIdx >= 0) edges.push({ id: `e-${ws.id}-lh-sm`, source: itemIds[lhIdx], target: itemIds[smIdx] });
      if (smIdx >= 0 && rptIdx >= 0) edges.push({ id: `e-${ws.id}-sm-rpt`, source: itemIds[smIdx], target: itemIds[rptIdx] });
      if (nbIdx >= 0 && rptIdx >= 0 && smIdx < 0) edges.push({ id: `e-${ws.id}-nb-rpt`, source: itemIds[nbIdx], target: itemIds[rptIdx] });

      wsYOffset += wsH + WS_GAP;
    });
  });

  return { nodes, edges };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

let _uid = 0;
const uid = () => `md-${Date.now()}-${_uid++}`;

function makeWorkspace(name = 'Workspace'): WizardWorkspace {
  return { id: uid(), name, items: ['fabric_lakehouse', 'fabric_pipeline', 'fabric_semantic_model', 'fabric_report'] };
}

function makeDomain(name: string, emoji: string): WizardDomain {
  return { id: uid(), name, emoji, workspaces: [makeWorkspace(`Workspace ${name}`)] };
}

// ─── Wizard component ─────────────────────────────────────────────────────────

const MultiDomainWizardModal: React.FC<Props> = ({ isOpen, onClose, onComplete }) => {
  const [step, setStep] = useState(1);
  const [state, setState] = useState<WizardState>({
    capacityName: 'Fabric Capacity (F32)',
    capacitySku: 'F32',
    domains: [
      makeDomain('Finance',   '💰'),
      makeDomain('IT / Data', '⚙️'),
    ],
  });

  // helpers to update nested state
  const updateDomain = (dId: string, patch: Partial<WizardDomain>) =>
    setState(s => ({ ...s, domains: s.domains.map(d => d.id === dId ? { ...d, ...patch } : d) }));

  const updateWorkspace = (dId: string, wsId: string, patch: Partial<WizardWorkspace>) =>
    setState(s => ({
      ...s,
      domains: s.domains.map(d => d.id !== dId ? d : {
        ...d,
        workspaces: d.workspaces.map(ws => ws.id === wsId ? { ...ws, ...patch } : ws),
      }),
    }));

  const toggleItem = (dId: string, wsId: string, itemType: string) => {
    setState(s => ({
      ...s,
      domains: s.domains.map(d => d.id !== dId ? d : {
        ...d,
        workspaces: d.workspaces.map(ws => ws.id !== wsId ? ws : {
          ...ws,
          items: ws.items.includes(itemType)
            ? ws.items.filter(t => t !== itemType)
            : [...ws.items, itemType],
        }),
      }),
    }));
  };

  const addDomain = () => {
    const next = SUGGESTED_DOMAINS.find(s => !state.domains.find(d => d.name === s.name));
    setState(s => ({ ...s, domains: [...s.domains, makeDomain(next?.name || 'Nouveau domaine', next?.emoji || '📁')] }));
  };

  const removeDomain = (dId: string) =>
    setState(s => ({ ...s, domains: s.domains.filter(d => d.id !== dId) }));

  const addWorkspace = (dId: string) =>
    setState(s => ({
      ...s,
      domains: s.domains.map(d => d.id !== dId ? d : {
        ...d,
        workspaces: [...d.workspaces, makeWorkspace('Nouveau Workspace')],
      }),
    }));

  const removeWorkspace = (dId: string, wsId: string) =>
    setState(s => ({
      ...s,
      domains: s.domains.map(d => d.id !== dId ? d : {
        ...d,
        workspaces: d.workspaces.filter(ws => ws.id !== wsId),
      }),
    }));

  const totalItems = state.domains.reduce((a, d) => a + d.workspaces.reduce((b, ws) => b + ws.items.length, 0), 0);
  const totalWs = state.domains.reduce((a, d) => a + d.workspaces.length, 0);

  const handleCreate = () => {
    const diagramData = generateDiagram(state);
    onComplete({ diagramData });
  };

  if (!isOpen) return null;

  const btnStyle = (active: boolean): React.CSSProperties => ({
    padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
    background: active ? '#1E3A5F' : '#E5E7EB',
    color: active ? 'white' : '#6B7280',
  });

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
    }}>
      <div style={{
        background: 'white', borderRadius: 16, width: '90%', maxWidth: 860,
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        {/* Header */}
        <div style={{ padding: '24px 28px 16px', borderBottom: '1px solid #E5E7EB' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#111827' }}>
                🏢 Template Multi-Domain
              </h2>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6B7280' }}>
                Configurez votre architecture Fabric par domaines métier
              </p>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9CA3AF', padding: 4 }}>✕</button>
          </div>

          {/* Step indicator */}
          <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
            {['Capacité', 'Domaines', 'Workspaces & Items'].map((label, i) => (
              <React.Fragment key={label}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 700,
                    background: step === i + 1 ? '#1E3A5F' : step > i + 1 ? '#059669' : '#E5E7EB',
                    color: step >= i + 1 ? 'white' : '#9CA3AF',
                  }}>
                    {step > i + 1 ? '✓' : i + 1}
                  </div>
                  <span style={{ fontSize: 12, fontWeight: step === i + 1 ? 600 : 400, color: step === i + 1 ? '#111827' : '#6B7280' }}>
                    {label}
                  </span>
                </div>
                {i < 2 && <div style={{ flex: 1, height: 1, background: step > i + 1 ? '#059669' : '#E5E7EB', maxWidth: 60 }} />}
              </React.Fragment>
            ))}
            {/* Summary chips */}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, fontSize: 11 }}>
              <span style={{ padding: '2px 8px', background: '#F3E8FF', color: '#7C3AED', borderRadius: 20, fontWeight: 600 }}>
                {state.domains.length} domaine{state.domains.length > 1 ? 's' : ''}
              </span>
              <span style={{ padding: '2px 8px', background: '#E8F5E9', color: '#059669', borderRadius: 20, fontWeight: 600 }}>
                {totalWs} workspace{totalWs > 1 ? 's' : ''}
              </span>
              <span style={{ padding: '2px 8px', background: '#EFF6FF', color: '#2563EB', borderRadius: 20, fontWeight: 600 }}>
                {totalItems} item{totalItems > 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>

          {/* ── Step 1: Capacity ── */}
          {step === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
                  Nom de la Capacity
                </label>
                <input
                  value={state.capacityName}
                  onChange={e => setState(s => ({ ...s, capacityName: e.target.value }))}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                  placeholder="Ex: Fabric Capacity Production"
                />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 8 }}>
                  SKU (taille de la Capacity)
                </label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {SKU_OPTIONS.map(sku => (
                    <button
                      key={sku}
                      onClick={() => setState(s => ({ ...s, capacitySku: sku }))}
                      style={{
                        padding: '8px 16px', borderRadius: 8, border: '2px solid',
                        borderColor: state.capacitySku === sku ? '#1E3A5F' : '#E5E7EB',
                        background: state.capacitySku === sku ? '#1E3A5F' : 'white',
                        color: state.capacitySku === sku ? 'white' : '#374151',
                        fontWeight: 600, cursor: 'pointer', fontSize: 14,
                      }}
                    >
                      {sku}
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: 12, color: '#9CA3AF', marginTop: 8 }}>
                  F2–F8 pour dev/test · F16–F32 pour production · F64+ pour enterprise
                </p>
              </div>

              {/* Preview box */}
              <div style={{ padding: 16, background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: 10 }}>
                <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 8, fontWeight: 600 }}>Aperçu</div>
                <div style={{
                  padding: '12px 16px', background: '#E0F7FA', border: '2px dashed #0097A7',
                  borderRadius: 8, fontSize: 13, color: '#006064', fontWeight: 600,
                }}>
                  ⚡ {state.capacityName || 'Fabric Capacity'} ({state.capacitySku})
                  <div style={{ fontSize: 11, fontWeight: 400, marginTop: 4, color: '#00838F' }}>
                    {state.domains.length} domaine{state.domains.length > 1 ? 's' : ''} · {totalWs} workspace{totalWs > 1 ? 's' : ''}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Step 2: Domains ── */}
          {step === 2 && (
            <div>
              <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 16 }}>
                Définissez les domaines métier. Chaque domaine regroupe des workspaces et des données par périmètre business.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                {state.domains.map((domain, dIdx) => (
                  <div key={domain.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '12px 14px', border: `2px solid ${DOMAIN_BORDER_COLORS[dIdx % DOMAIN_BORDER_COLORS.length]}`,
                    borderRadius: 10, background: DOMAIN_COLORS[dIdx % DOMAIN_COLORS.length],
                  }}>
                    {/* Emoji picker (simplified — input) */}
                    <input
                      value={domain.emoji}
                      onChange={e => updateDomain(domain.id, { emoji: e.target.value })}
                      style={{ width: 40, textAlign: 'center', fontSize: 20, border: '1px solid #D1D5DB', borderRadius: 6, padding: 4, background: 'white' }}
                    />
                    <input
                      value={domain.name}
                      onChange={e => updateDomain(domain.id, { name: e.target.value })}
                      style={{ flex: 1, padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, background: 'white' }}
                      placeholder="Nom du domaine"
                    />
                    <span style={{ fontSize: 11, color: '#6B7280', whiteSpace: 'nowrap' }}>
                      {domain.workspaces.length} ws
                    </span>
                    <button
                      onClick={() => removeDomain(domain.id)}
                      disabled={state.domains.length <= 1}
                      style={{
                        background: 'none', border: 'none', cursor: state.domains.length <= 1 ? 'not-allowed' : 'pointer',
                        color: state.domains.length <= 1 ? '#D1D5DB' : '#EF4444', fontSize: 16, padding: 4,
                      }}
                      title="Supprimer ce domaine"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>

              {state.domains.length < 7 && (
                <button
                  onClick={addDomain}
                  style={{
                    padding: '10px 18px', border: '2px dashed #D1D5DB', borderRadius: 10,
                    background: 'white', cursor: 'pointer', fontSize: 13, color: '#6B7280',
                    width: '100%', fontWeight: 600,
                  }}
                >
                  + Ajouter un domaine
                </button>
              )}

              {/* Suggestions */}
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 8 }}>Suggestions rapides :</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {SUGGESTED_DOMAINS.filter(s => !state.domains.find(d => d.name === s.name)).map(s => (
                    <button
                      key={s.name}
                      onClick={() => setState(prev => ({ ...prev, domains: [...prev.domains, makeDomain(s.name, s.emoji)] }))}
                      style={{
                        padding: '4px 10px', borderRadius: 20, border: '1px solid #E5E7EB',
                        background: 'white', cursor: 'pointer', fontSize: 12, color: '#374151',
                      }}
                    >
                      {s.emoji} {s.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── Step 3: Workspaces & Items ── */}
          {step === 3 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {state.domains.map((domain, dIdx) => (
                <div key={domain.id} style={{
                  border: `2px solid ${DOMAIN_BORDER_COLORS[dIdx % DOMAIN_BORDER_COLORS.length]}`,
                  borderRadius: 12, overflow: 'hidden',
                }}>
                  {/* Domain header */}
                  <div style={{
                    padding: '10px 16px', background: DOMAIN_COLORS[dIdx % DOMAIN_COLORS.length],
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>{domain.emoji} {domain.name}</span>
                    <button
                      onClick={() => addWorkspace(domain.id)}
                      style={{
                        padding: '4px 12px', borderRadius: 6, border: '1px solid #D1D5DB',
                        background: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#374151',
                      }}
                    >
                      + Workspace
                    </button>
                  </div>

                  {/* Workspaces */}
                  <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {domain.workspaces.map(ws => (
                      <div key={ws.id} style={{
                        border: '1px solid #D1FAE5', borderRadius: 10,
                        background: '#F0FDF4', padding: 12,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                          <img src="/icons/fabric/group_workspace_64_non-item.svg" alt="" style={{ width: 18, height: 18 }} />
                          <input
                            value={ws.name}
                            onChange={e => updateWorkspace(domain.id, ws.id, { name: e.target.value })}
                            style={{ flex: 1, padding: '5px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, background: 'white' }}
                            placeholder="Nom du workspace"
                          />
                          <button
                            onClick={() => removeWorkspace(domain.id, ws.id)}
                            disabled={domain.workspaces.length <= 1}
                            style={{
                              background: 'none', border: 'none',
                              cursor: domain.workspaces.length <= 1 ? 'not-allowed' : 'pointer',
                              color: domain.workspaces.length <= 1 ? '#D1D5DB' : '#EF4444', fontSize: 14,
                            }}
                          >
                            ✕
                          </button>
                        </div>

                        {/* Items grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                          {ITEM_CATALOG.map(catalogItem => {
                            const selected = ws.items.includes(catalogItem.type);
                            return (
                              <button
                                key={catalogItem.type}
                                onClick={() => toggleItem(domain.id, ws.id, catalogItem.type)}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 6,
                                  padding: '5px 8px', borderRadius: 6, cursor: 'pointer',
                                  border: `1px solid ${selected ? '#059669' : '#E5E7EB'}`,
                                  background: selected ? '#ECFDF5' : 'white',
                                  fontSize: 11, fontWeight: selected ? 600 : 400,
                                  color: selected ? '#059669' : '#6B7280',
                                }}
                              >
                                <img src={ITEM_ICONS[catalogItem.type]} alt="" style={{ width: 14, height: 14 }} />
                                {catalogItem.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 28px', borderTop: '1px solid #E5E7EB',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <button onClick={step === 1 ? onClose : () => setStep(s => s - 1)} style={btnStyle(false)}>
            {step === 1 ? 'Annuler' : '← Précédent'}
          </button>

          {step < 3 ? (
            <button onClick={() => setStep(s => s + 1)} style={btnStyle(true)}>
              Suivant →
            </button>
          ) : (
            <button
              onClick={handleCreate}
              disabled={totalWs === 0}
              style={{
                ...btnStyle(true),
                background: totalWs === 0 ? '#9CA3AF' : '#059669',
                cursor: totalWs === 0 ? 'not-allowed' : 'pointer',
              }}
            >
              ✓ Générer l'architecture
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MultiDomainWizardModal;
